// One money formatter for the whole product.
//
// Prices used to be rendered with a hardcoded `₹` and `toFixed(2)` in a dozen
// places. That was fine while every charge was in rupees — and became wrong the
// moment a country override could charge in dollars or yen: a $49 invoice
// rendered as "₹49.00", and a ¥5,000 one as "₹5000.00" when yen has no decimal
// places at all.
//
// Intl.NumberFormat already knows every currency's symbol, separator placement
// and minor units, so it is the only thing here that formats. The backend's
// `core/currency.py` owns the same facts for the server side; the two never need
// to agree on anything but the ISO code, which is what travels between them.

// The base currency the platform lists its prices in. Mirrors
// backend/core/currency.py BASE_CURRENCY.
export const BASE_CURRENCY = "INR";

// A currency is formatted for the locale that writes it most naturally: an
// Indian reader expects "₹1,23,456" (lakh grouping), not "₹123,456".
const LOCALE_BY_CURRENCY = {
  INR: "en-IN",
  USD: "en-US",
  GBP: "en-GB",
  EUR: "de-DE",
  JPY: "ja-JP",
  KRW: "ko-KR",
  CNY: "zh-CN",
  BRL: "pt-BR",
  CHF: "de-CH",
};

function localeFor(currency) {
  return LOCALE_BY_CURRENCY[currency] || "en-US";
}

/**
 * Format an amount in its own currency.
 *
 * `Intl` supplies the right number of decimals per currency, so nothing here
 * assumes two: JPY renders as "¥5,000" and KWD as "KD 15.500" without any
 * per-currency branching at the call site.
 *
 * A currency code the browser doesn't recognise would make Intl throw and take
 * the page down, so an unknown code degrades to a plain number with the code
 * appended — visibly odd, which is the correct outcome for bad data, but not a
 * blank screen.
 */
export function formatMoney(amount, currency = BASE_CURRENCY, { compact = false } = {}) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  const code = String(currency || BASE_CURRENCY).toUpperCase();
  try {
    return new Intl.NumberFormat(localeFor(code), {
      style: "currency",
      currency: code,
      ...(compact ? { notation: "compact", maximumFractionDigits: 1 } : {}),
    }).format(value);
  } catch (e) {
    return `${value.toLocaleString()} ${code}`;
  }
}

/** Just the symbol — for prefixing an input the user types a bare number into. */
export function currencySymbol(currency = BASE_CURRENCY) {
  const code = String(currency || BASE_CURRENCY).toUpperCase();
  try {
    return new Intl.NumberFormat(localeFor(code), { style: "currency", currency: code })
      .formatToParts(0)
      .filter((p) => p.type === "currency")
      .map((p) => p.value)
      .join("") || code;
  } catch (e) {
    return code;
  }
}

/**
 * How many decimal places this currency actually has.
 *
 * Drives the `step` on a price input, so an operator cannot type ¥499.50 — an
 * amount that does not exist and that the gateway would reject. Mirrors the
 * `exponent` in backend/core/currency.py; when the admin page has the served
 * currency catalog it should prefer that, and this is the offline fallback.
 */
export function currencyDecimals(currency = BASE_CURRENCY) {
  const code = String(currency || BASE_CURRENCY).toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: code })
      .resolvedOptions().maximumFractionDigits;
  } catch (e) {
    return 2;
  }
}

/** The `step` attribute for a number input in this currency. */
export function currencyStep(currency = BASE_CURRENCY) {
  const d = currencyDecimals(currency);
  return d === 0 ? "1" : `0.${"0".repeat(d - 1)}1`;
}
